import React, { useState } from 'react';
import { Plus, Trash2, Mail, AlertCircle, SlidersHorizontal, UserPlus, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "../../services/contexts/authContext";
import { useGetTeamMembers, useDeleteTeamMember } from "../../custom-hooks/use-users";
import { useConfirm } from "../../custom-hooks/confirm-dialog";
import { BottomUXSpace } from "../../components";
import AddTeamMemberModal from "./AddTeamMemberModal";
import { Notify } from "../../utils/functions";
import { AddTeamMember } from '../../services/backend';
import { SendResetPasswordEmail } from '../../services/firebase/auth';

const CustomerTeam = () => {
    const { userDetails } = useAuth();
    const customerId = userDetails?._id;

    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [isAdding, setIsAdding] = useState(false);

    const {
        data: teamMembers,
        isLoading: isTeamMembersLoading,
        error: teamMembersError,
        refetch: refetchTeamMembers,
    } = useGetTeamMembers(customerId);

    const deleteUserMutation = useDeleteTeamMember();
    const { confirm, ConfirmationDialog } = useConfirm();

    const maxTeamMembers = userDetails?.customerProfile?.maxTeamMembers || 5;
    const currentTeamCount = teamMembers?.users?.length || 0;
    const canAddMore = currentTeamCount < maxTeamMembers;

    const handleAddMember = async (formData) => {
        try {
            setIsAdding(true);
            await AddTeamMember({
                email: formData.email,
                name: formData.name,
                cnic: formData.cnic,
            });

            try {
                await SendResetPasswordEmail(formData.email);
            } catch (emailError) {
                console.error("Failed to send reset password email", emailError);
            }

            Notify("Team member added successfully", "success");
            refetchTeamMembers();
            setIsAddModalOpen(false);
        } catch (error) {
            console.error("Failed to add team member", error);
            throw error;
        } finally {
            setIsAdding(false);
        }
    };

    const handleDeleteMember = async (memberId, memberName) => {
        const confirmed = await confirm({
            title: "Remove Team Member",
            message: `Are you sure you want to remove ${memberName} from your team? They will no longer have access to the dashboard.`,
            confirmText: "Remove",
            confirmColor: "red"
        });

        if (confirmed) {
            try {
                await deleteUserMutation.mutateAsync(memberId);
                refetchTeamMembers();
            } catch (error) {
                console.error("Failed to delete user", error);
            }
        }
    };

    if (isTeamMembersLoading) {
        return (
            <div className="flex h-[80vh] items-center justify-center">
                <Loader2 className="h-10 w-10 animate-spin text-brand" />
            </div>
        );
    }

    const members = teamMembers?.users || [];

    return (
        <div className="animate-fadeIn px-4 py-5 md:px-[34px] md:py-[30px]">
            {/* Header */}
            <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div>
                    <h1 className="font-display text-[25px] font-bold text-ink">Team Management</h1>
                    <p className="mt-1 max-w-2xl text-sm text-[#8C8175]">
                        Manage your team members and their access permissions. You can add up to {maxTeamMembers} members to your team.
                    </p>
                </div>

                <div className="flex shrink-0 items-center gap-4 rounded-2xl border border-[#ECE3D5] bg-white py-2 pl-5 pr-2">
                    <div className="text-center">
                        <div className="font-display text-xl font-bold leading-none text-ink">
                            {currentTeamCount} <span className="text-lg font-normal text-[#A89C8C]">/ {maxTeamMembers}</span>
                        </div>
                        <div className="mt-1 text-xs font-medium text-[#8C8175]">Team Members</div>
                    </div>
                    <div className="h-10 w-px bg-[#ECE3D5]" />
                    <button
                        onClick={() => setIsAddModalOpen(true)}
                        disabled={!canAddMore}
                        className="flex items-center gap-2 rounded-[11px] bg-brand px-4 py-2.5 font-display text-[13px] font-semibold text-white shadow-[0_8px_16px_-10px_rgba(190,83,16,0.7)] transition-colors hover:bg-brand-dark disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        <Plus size={18} />
                        Add Member
                    </button>
                </div>
            </div>

            {/* Warning if limit reached */}
            {!canAddMore && (
                <div className="mb-6 flex items-center gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4 text-amber-800">
                    <AlertCircle size={20} className="shrink-0 text-amber-600" />
                    <span className="text-sm font-medium">You have reached the maximum limit of {maxTeamMembers} team members. To add more, please contact support or upgrade your plan.</span>
                </div>
            )}

            {teamMembersError ? (
                <div className="rounded-2xl border border-red-100 bg-red-50 p-8 text-center text-red-600">
                    <AlertCircle size={40} className="mx-auto mb-4 opacity-50" />
                    <h6 className="font-display text-lg font-semibold">Failed to load team members</h6>
                    <p className="mt-1 text-sm opacity-80">Please try refreshing the page</p>
                </div>
            ) : (
                <div className="grid gap-[18px]" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(330px, 1fr))' }}>
                    {members.map((member) => {
                        const monthlyHrs = (member?.meetingRoomQuota?.monthlyHours || 0) / 60;
                        const usedHrs = (member?.meetingRoomQuota?.usedHours || 0) / 60;
                        return (
                            <div
                                key={member._id}
                                className="animate-fadeIn rounded-2xl border border-[#ECE3D5] bg-white p-5 transition-all hover:-translate-y-0.5 hover:shadow-[0_16px_36px_-22px_rgba(40,22,8,0.45)]"
                            >
                                <div className="flex items-start gap-3">
                                    <div className="flex h-[46px] w-[46px] shrink-0 items-center justify-center rounded-[13px] bg-cream-input font-display text-[17px] font-bold text-brand">
                                        {member.name?.charAt(0).toUpperCase()}
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-2">
                                            <div className="truncate font-display text-[15px] font-semibold text-ink">{member.name}</div>
                                            <span className="shrink-0 rounded-full bg-cream-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#8C8175]">Member</span>
                                        </div>
                                        <div className="mt-0.5 flex items-center gap-1.5 text-[12.5px] text-[#8C8175]">
                                            <Mail size={14} className="shrink-0 text-[#B7AC9E]" />
                                            <span className="truncate">{member.email}</span>
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => handleDeleteMember(member._id, member.name)}
                                        aria-label="Remove member"
                                        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[9px] text-[#C0907F] transition-colors hover:bg-[#FAE6E1] hover:text-[#BE3B28]"
                                    >
                                        <Trash2 size={18} />
                                    </button>
                                </div>

                                <div className="mt-4 flex items-center gap-3.5 rounded-xl border border-[#F1E9DC] bg-cream px-3.5 py-3">
                                    <div className="flex-1 text-center">
                                        <div className="font-display text-sm font-bold text-ink">{monthlyHrs} hrs</div>
                                        <div className="mt-0.5 text-[10.5px] text-[#A89C8C]">Monthly cap</div>
                                    </div>
                                    <div className="h-7 w-px bg-[#ECE3D5]" />
                                    <div className="flex-1 text-center">
                                        <div className="font-display text-sm font-bold text-ink">{usedHrs.toFixed(1)} hrs</div>
                                        <div className="mt-0.5 text-[10.5px] text-[#A89C8C]">Used this month</div>
                                    </div>
                                </div>

                                <Link to={`/c/team/${member._id}`}>
                                    <button className="mt-3.5 flex w-full items-center justify-center gap-2 rounded-[11px] border-[1.5px] border-[#E6DDCF] bg-white py-2.5 font-display text-[13px] font-semibold text-brand transition-colors hover:border-[#E0C3A4] hover:bg-cream-input">
                                        <SlidersHorizontal size={17} />
                                        Manage Settings
                                    </button>
                                </Link>
                            </div>
                        );
                    })}

                    {canAddMore && (
                        <button
                            onClick={() => setIsAddModalOpen(true)}
                            className="flex min-h-[150px] flex-col items-center justify-center rounded-2xl border-[1.5px] border-dashed border-[#DECFBC] p-5 text-center transition-colors hover:border-[#E0C3A4] hover:bg-[#FBF6EF]"
                        >
                            <div className="flex h-[46px] w-[46px] items-center justify-center rounded-[13px] bg-cream-input text-brand">
                                <UserPlus size={24} />
                            </div>
                            <div className="mt-3 font-display text-sm font-semibold text-ink">Add a team member</div>
                            <div className="mt-1 text-xs text-[#8C8175]">{currentTeamCount} of {maxTeamMembers} seats used</div>
                        </button>
                    )}
                </div>
            )}

            <AddTeamMemberModal
                isOpen={isAddModalOpen}
                onClose={() => setIsAddModalOpen(false)}
                onAdd={handleAddMember}
                isAdding={isAdding}
            />

            <ConfirmationDialog />
            <BottomUXSpace />
        </div>
    );
};

export default CustomerTeam;
